import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AccountInput,
  EntryInput,
  EntryListing,
  KasaAccount,
  KasaEntry,
  MonthlySummaryRow,
  NetWorthSnapshot,
  NetWorthSummary,
  TransferInput,
} from '@omnia/shared';
import { api, shouldRetry } from '../../lib/api.ts';

export const NET_WORTH_REFRESH_MS = 5 * 60_000;

export const useAccounts = () =>
  useQuery({
    queryKey: ['kasa', 'accounts'],
    queryFn: ({ signal }) => api<KasaAccount[]>('/api/kasa/accounts', { signal }),
    retry: shouldRetry,
  });

export const useCategories = () =>
  useQuery({
    queryKey: ['kasa', 'categories'],
    queryFn: ({ signal }) => api<{ categories: string[] }>('/api/kasa/categories', { signal }),
    select: (data) => data.categories,
    retry: shouldRetry,
  });

export interface EntryFilter {
  from: string;
  to: string;
  account: string;
  category: string;
  q: string;
  limit: number;
}

export function useEntries(filter: EntryFilter) {
  const params = new URLSearchParams({ limit: String(filter.limit) });
  if (filter.from) params.set('from', filter.from);
  if (filter.to) params.set('to', filter.to);
  if (filter.account) params.set('account', filter.account);
  if (filter.category) params.set('category', filter.category);
  if (filter.q) params.set('q', filter.q);
  return useQuery({
    queryKey: ['kasa', 'entries', params.toString()],
    queryFn: ({ signal }) => api<EntryListing>(`/api/kasa/entries?${params}`, { signal }),
    placeholderData: (previous) => previous,
    retry: shouldRetry,
  });
}

/** Also read by the info bar's net worth widget, which is why it lives with the queries. */
export const useNetWorth = (enabled = true) =>
  useQuery({
    queryKey: ['kasa', 'networth'],
    queryFn: ({ signal }) => api<NetWorthSummary>('/api/kasa/networth', { signal }),
    enabled,
    retry: shouldRetry,
    refetchInterval: NET_WORTH_REFRESH_MS,
    staleTime: NET_WORTH_REFRESH_MS / 2,
  });

export const useSnapshots = (days: number) =>
  useQuery({
    queryKey: ['kasa', 'snapshots', days],
    queryFn: ({ signal }) => api<NetWorthSnapshot[]>(`/api/kasa/snapshots?days=${days}`, { signal }),
    retry: shouldRetry,
  });

export const useMonthly = (year: number) =>
  useQuery({
    queryKey: ['kasa', 'monthly', year],
    queryFn: ({ signal }) => api<MonthlySummaryRow[]>(`/api/kasa/summary/monthly?year=${year}`, { signal }),
    retry: shouldRetry,
  });

/** Every ledger write refreshes everything derived from it: balances, lists, net worth, charts. */
export function useKasaWrites() {
  const client = useQueryClient();
  const refresh = () => client.invalidateQueries({ queryKey: ['kasa'] });
  return {
    saveAccount: useMutation({
      mutationFn: (input: { id?: string; value: Partial<AccountInput> }) =>
        input.id
          ? api<KasaAccount>(`/api/kasa/accounts/${input.id}`, { method: 'PATCH', body: input.value })
          : api<KasaAccount>('/api/kasa/accounts', { method: 'POST', body: input.value }),
      onSuccess: refresh,
    }),
    removeAccount: useMutation({
      mutationFn: (id: string) => api<void>(`/api/kasa/accounts/${id}`, { method: 'DELETE' }),
      onSuccess: refresh,
    }),
    addEntry: useMutation({
      mutationFn: (input: EntryInput) => api<KasaEntry>('/api/kasa/entries', { method: 'POST', body: input }),
      onSuccess: refresh,
    }),
    patchEntry: useMutation({
      mutationFn: (input: { id: string; value: Partial<EntryInput> }) =>
        api<KasaEntry>(`/api/kasa/entries/${input.id}`, { method: 'PATCH', body: input.value }),
      onSuccess: refresh,
    }),
    removeEntry: useMutation({
      mutationFn: (id: string) => api<void>(`/api/kasa/entries/${id}`, { method: 'DELETE' }),
      onSuccess: refresh,
    }),
    addTransfer: useMutation({
      mutationFn: (input: TransferInput) => api<KasaEntry[]>('/api/kasa/transfers', { method: 'POST', body: input }),
      onSuccess: refresh,
    }),
    saveCategories: useMutation({
      mutationFn: (categories: string[]) => api<{ categories: string[] }>('/api/kasa/categories', { method: 'PUT', body: { categories } }),
      onSuccess: refresh,
    }),
  };
}
